import { Price, PriceItem, UnitPrice } from './price.model';
import { Seller } from './seller.model';
import { ArraySerializer, deserialize, Field, Model, PrimitiveSerializer, Type } from 'serialize-ts';
import { Organization } from './organization.model';
import { VolumePrice } from './volume-price.model';
import { BusinessModel } from './business-model.model';
import { OfferService } from './offer-service.model';
import { ModelMetadataSerializer } from 'serialize-ts/dist/serializers/model-metadata.serializer';
import { ReferencePrice } from '~app/modules/product/models/reference-price.model';
import { ProductIndexOffer } from '~app/modules/product/models/product-index-offer.model';
import { OfferLabels } from '~app/modules/product/enums/offer-labels.enum';
import { ProductIndexPrice } from '~app/modules/product/models/product-index-price.model';
import { ProductIndexTime } from '~app/modules/product/models/product-index-time.interface';
import { ProductIndexRegionInfo } from '~app/modules/product/models/product-index-region-info.model';
import { AddToCartReferencePrice } from '~shared/modules/shopping-cart-global/models';
import { ShippingGroup } from '~app/modules/product/models/shipping-group.model';
import { VAS } from './value-added-service.model';

@Model()
export class Offer extends ProductIndexOffer {
  @Field()
  id: string;

  @Field()
  price: PriceItem;

  @Field()
  priceOrigin?: PriceItem;

  @Field()
  basePrice: PriceItem;

  @Field()
  basePriceOrigin?: PriceItem;

  @Field()
  baseUnit: string;

  @Field()
  pricePerUnit?: PriceItem;

  @Field()
  unitPrice?: UnitPrice;

  @Field()
  pricePerUnitOrigin?: PriceItem;

  @Field()
  minShipmentPrice: Price;

  @Field()
  shippingPrice?: PriceItem; // TODO: Remove optional when removing FF for shipping

  @Field()
  seller: Seller;

  @Field()
  quantity: number;

  @Field()
  minShippingTime: number;

  @Field()
  maxShippingTime: number;

  @Field()
  organization: Organization;

  @Field()
  originRegionId: string;

  @Field()
  destinationRegionId: string;

  @Field()
  businessModel: BusinessModel;

  @Field()
  freightForwarding: boolean;

  @Field()
  hasPromotion: boolean;

  @Field()
  @Type(new ArraySerializer(new PrimitiveSerializer()))
  labels: OfferLabels[];

  @Field()
  @Type(new ArraySerializer(new ModelMetadataSerializer(VolumePrice)))
  volumePrice: VolumePrice[];

  @Field()
  @Type(new ArraySerializer(new ModelMetadataSerializer(VolumePrice)))
  volumePriceOrigin?: VolumePrice[];

  @Field()
  offerType?: string;

  // Todo: this should not be optional - needs fixing unit tests
  @Field()
  referencePrice?: ReferencePrice;

  @Field()
  shippingGroup?: ShippingGroup;

  @Field()
  @Type(new ArraySerializer(new ModelMetadataSerializer(VAS)))
  valueAddedServices?: VAS[];

  public static mapOffer(productIndexOffer: ProductIndexOffer): Offer {
    const originRegionInfo: ProductIndexRegionInfo = productIndexOffer.originRegionInfo;
    const destinationRegionInfo: ProductIndexRegionInfo = productIndexOffer.destinationRegionInfo;
    const shippingTime: ProductIndexTime = destinationRegionInfo?.shipping?.time;
    const shippingPrice: ProductIndexPrice = destinationRegionInfo?.shipping?.price;
    try {
      return !!productIndexOffer ? deserialize({
        ...productIndexOffer,
        price: PriceItem.mapPriceItem(destinationRegionInfo?.price),
        priceOrigin: PriceItem.mapPriceItem(originRegionInfo?.price),
        ...{
          basePrice: destinationRegionInfo?.basePrice
            && PriceItem.mapPriceItem(productIndexOffer?.destinationRegionInfo?.basePrice?.price)
        },
        ...{
          basePriceOrigin: originRegionInfo?.basePrice &&
            PriceItem.mapPriceItem(productIndexOffer?.originRegionInfo?.basePrice?.price)
        },
        ...{ baseUnit: destinationRegionInfo?.basePrice && destinationRegionInfo?.basePrice.unit },
        ...{
          unitPrice: destinationRegionInfo?.unitPrice && {
            price: PriceItem.mapPriceItem(destinationRegionInfo?.unitPrice?.price),
            unitType: Number(destinationRegionInfo?.unitPrice?.unitType)
          }
        },
        ...{ minShipmentPrice: shippingPrice && { amount: Number(shippingPrice.net),currency: shippingPrice.currency }},
        ...{ shippingPrice: shippingPrice &&PriceItem.mapPriceItem(shippingPrice)},
        minShippingTime: Number(shippingTime?.from),
        maxShippingTime: Number(shippingTime?.to),
        originRegionId: originRegionInfo?.region?.value,
        destinationRegionId: destinationRegionInfo.region.value,
        volumePrice: destinationRegionInfo?.volumePrices,
        volumePriceOrigin: originRegionInfo?.volumePrices,
        offerServices: !!productIndexOffer.offerServices ?
          OfferService.mapOfferServices(productIndexOffer.offerServices) : [],
        offerType: '',
        ...{
          referencePrice: destinationRegionInfo?.referencePrice
            && ReferencePrice.mapReferencePrice(productIndexOffer)
        },
        valueAddedServices: productIndexOffer?.valueAddedServices,
      }, Offer) : null;
    } catch (error) {
      throw new Error('MapOffer failed');
    }
  }

  public static mapReferencePriceToCartReferencePrice(data: ProductIndexPrice): AddToCartReferencePrice {
    return {
      currency: data.currency,
      offerTax: data.vatRate,
      totalNet: data.net,
      totalGross: data.gross,
      discountedPercentage: data.discountPercentage,
      priceType: data.priceType
    };
  }
}
